/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { cn } from '@/shared/ui/utils/tailwind';

export interface SwitchProps {
  checked: boolean;
  label: string;
  description?: string;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

/**
 * An on/off control.
 *
 * The checkbox answers "is this selected"; this answers "is this running",
 * which is the question every row in the Google settings asks. The whole row
 * is the control, so the label is clickable without a separate hit target.
 *
 * @param props - State, labels and the change handler.
 * @returns The rendered switch.
 */
export const Switch: React.FC<SwitchProps> = ({
  checked,
  label,
  description,
  disabled = false,
  onChange,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-disabled={disabled || undefined}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={cn(
      'flex w-full items-center justify-between gap-(--space-4) text-left',
      'rounded-(--radius-md) px-(--space-1) py-(--space-2)',
      'font-[family-name:var(--font-family)]',
      disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer',
    )}
  >
    <span className="flex min-w-0 flex-col gap-(--space-0-5)">
      <span className="text-[length:var(--text-body-sm)] text-[var(--text-primary)]">
        {label}
      </span>
      {description && (
        <span className="text-[length:var(--text-meta)] text-[var(--text-tertiary-soft)] text-pretty">
          {description}
        </span>
      )}
    </span>
    <span
      aria-hidden="true"
      className="relative shrink-0 rounded-full transition-colors"
      style={{
        width: 'var(--switch-width)',
        height: 'var(--switch-height)',
        background: checked
          ? 'var(--switch-track-on)'
          : 'var(--switch-track-off)',
      }}
    >
      <span
        className="absolute top-1/2 block -translate-y-1/2 rounded-full transition-[left]"
        style={{
          width: 'var(--switch-thumb-size)',
          height: 'var(--switch-thumb-size)',
          background: 'var(--switch-thumb)',
          boxShadow: 'var(--switch-thumb-shadow)',
          left: checked
            ? 'calc(var(--switch-width) - var(--switch-thumb-size) - 2px)'
            : '2px',
        }}
      />
    </span>
  </button>
);
